/**
 * Utility functions for formatting data
 */

/**
 * Format price in crystals with Russian locale formatting (spaces for thousands)
 * @param {number|string} price - Price in crystals
 * @returns {string} Formatted price string (e.g., "6 300", "1 250 000")
 */
export const formatPrice = (price) => {
  return Number(price).toLocaleString('ru-RU')
}

/**
 * Format number with locale string (adds commas for thousands)
 * @param {number} number - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (number) => {
  return number.toLocaleString()
}

const WEAR_SHORTCUTS = {
  'Factory New': 'FN',
  'Minimal Wear': 'MW',
  'Field-Tested': 'FT',
  'Well-Worn': 'WW',
  'Battle-Scarred': 'BS'
}

export const shortenSkinWear = (text = '') => {
  let formatted = String(text)

  Object.entries(WEAR_SHORTCUTS).forEach(([full, short]) => {
    const withBracketsPattern = new RegExp(`\\(${full}\\)`, 'gi')
    formatted = formatted.replace(withBracketsPattern, short)
  })

  return formatted
}

/**
 * Форматирование минут в компактный формат (дни/часы/минуты)
 * @param {number} minutes - Количество минут
 * @param {function} t - Функция перевода i18n (useTranslation)
 * @returns {string} Компактная строка (например, "2д 5ч", "3ч 15м", "10м")
 */
export const formatMinutesLeft = (minutes, t) => {
  if (minutes <= 0) return t('timeShort.zero', '0м')
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days}${t('timeShort.d', 'д')} ${hours}${t('timeShort.h', 'ч')}`
  if (hours > 0) return `${hours}${t('timeShort.h', 'ч')} ${mins}${t('timeShort.m', 'м')}`
  return `${mins}${t('timeShort.m', 'м')}`
}

/**
 * Форматирование секунд в компактный формат (дни/часы/минуты/секунды)
 * @param {number} seconds - Количество секунд
 * @param {function} t - Функция перевода i18n (useTranslation)
 * @returns {string} Компактная строка (например, "2д 5ч", "3ч 15м", "10м 5с", "42с")
 */
export const formatSecondsLeft = (seconds, t) => {
  if (seconds <= 0) return t('timeShort.zero', '0м')
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (days > 0) return `${days}${t('timeShort.d', 'д')} ${hours}${t('timeShort.h', 'ч')}`
  if (hours > 0) return `${hours}${t('timeShort.h', 'ч')} ${mins}${t('timeShort.m', 'м')}`
  if (mins > 0) return `${mins}${t('timeShort.m', 'м')} ${secs}${t('timeShort.s', 'с')}`
  return `${secs}${t('timeShort.s', 'с')}`
}

export const parseSkinName = (name = '') => {
  const raw = String(name || '').trim()

  if (!raw) {
    return {
      raw: '',
      isStatTrak: false,
      hasVariant: false,
      title: '',
      subtitleFull: '',
      subtitleCompact: '',
      wearFull: '',
      wearShort: ''
    }
  }

  const statTrakMatch = raw.match(/^StatTrak™\s*/i)
  const isStatTrak = Boolean(statTrakMatch)
  const withoutStatTrak = isStatTrak ? raw.replace(/^StatTrak™\s*/i, '').trim() : raw

  const parts = withoutStatTrak.split('|').map((part) => part.trim())
  const hasVariant = parts.length > 1

  if (!hasVariant) {
    return {
      raw,
      isStatTrak,
      hasVariant: false,
      title: raw,
      subtitleFull: '',
      subtitleCompact: '',
      wearFull: '',
      wearShort: ''
    }
  }

  const baseName = parts[0]
  const rightPart = parts.slice(1).join(' | ').trim()
  const wearMatch = rightPart.match(/\(([^()]+)\)\s*$/)

  const wearFull = wearMatch ? wearMatch[1].trim() : ''
  const wearShort = WEAR_SHORTCUTS[wearFull] || ''
  const skinName = wearMatch ? rightPart.replace(/\(([^()]+)\)\s*$/, '').trim() : rightPart
  const title = isStatTrak ? `StatTrak™ ${baseName}` : baseName

  return {
    raw,
    isStatTrak,
    hasVariant: true,
    title,
    subtitleFull: wearFull ? `${skinName} (${wearFull})` : skinName,
    subtitleCompact: wearShort ? `${skinName} • ${wearShort}` : skinName,
    wearFull,
    wearShort
  }
}
